import React, { useState } from "react"
import { Input } from "@aix/ui"

interface Comment {
  id: string
  body: string
  authorId: string
  authorName?: string
  createdAt: string
  updatedAt: string
}

interface TaskDetailsProps {
  taskId: string
  isLoading: boolean
  comments: Comment[]
  onClose: () => void
  onUpdateTask: (taskId: string, data: any) => void
  onAddComment: (body: string) => void
  updateTaskLoading: boolean
  addCommentLoading: boolean
}

export const TaskDetails: React.FC<TaskDetailsProps> = ({
  taskId,
  isLoading,
  comments,
  onClose,
  onUpdateTask,
  onAddComment,
  updateTaskLoading,
  addCommentLoading,
}) => {
  const [commentText, setCommentText] = useState("")
  const [editMode, setEditMode] = useState<Record<string, boolean>>({})
  const [editValues, setEditValues] = useState<Record<string, string>>({})

  const handleAddComment = () => {
    if (commentText.trim()) {
      onAddComment(commentText.trim())
      setCommentText("")
    }
  }

  const handleEditToggle = (field: string, currentValue: string) => {
    setEditMode((prev) => ({ ...prev, [field]: !prev[field] }))
    setEditValues((prev) => ({ ...prev, [field]: currentValue }))
  }

  const handleSaveEdit = (field: string) => {
    const value = editValues[field]
    if (value !== undefined) {
      onUpdateTask(taskId, { [field]: value })
      setEditMode((prev) => ({ ...prev, [field]: false }))
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent, field: string) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      if (field === "comment") {
        handleAddComment()
      } else {
        handleSaveEdit(field)
      }
    }
    if (e.key === "Escape") {
      setEditMode((prev) => ({ ...prev, [field]: false }))
    }
  }

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg border p-4">
        <div className="flex justify-between items-center mb-4">
          <div className="h-6 bg-muted rounded w-1/3" />
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            ✕
          </button>
        </div>
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded" />
          <div className="h-4 bg-muted rounded w-3/4" />
          <div className="h-32 bg-muted rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-card rounded-lg border p-4 h-fit">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Task Details</h3>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
          ✕
        </button>
      </div>

      {/* Task Information */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="text-sm font-medium text-muted-foreground">Title</label>
          <div className="mt-1">
            {editMode.title ? (
              <Input
                value={editValues.title || ""}
                onChange={(e) => setEditValues((prev) => ({ ...prev, title: e.target.value }))}
                onKeyDown={(e) => handleKeyPress(e, "title")}
                onBlur={() => setEditMode((prev) => ({ ...prev, title: false }))}
                className="w-full"
                autoFocus
              />
            ) : (
              <div
                onClick={() => handleEditToggle("title", "Current Title")}
                className="cursor-pointer hover:bg-muted p-2 rounded"
              >
                Task Title (Click to edit)
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-muted-foreground">Description</label>
          <div className="mt-1">
            {editMode.body ? (
              <textarea
                value={editValues.body || ""}
                onChange={(e) => setEditValues((prev) => ({ ...prev, body: e.target.value }))}
                onKeyDown={(e) => handleKeyPress(e, "body")}
                onBlur={() => setEditMode((prev) => ({ ...prev, body: false }))}
                className="w-full p-2 border border-border rounded-md resize-none"
                rows={3}
                autoFocus
              />
            ) : (
              <div
                onClick={() => handleEditToggle("body", "Current Description")}
                className="cursor-pointer hover:bg-muted p-2 rounded min-h-[60px]"
              >
                Task description (Click to edit)
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground">Status</label>
            <select
              onChange={(e) => onUpdateTask(taskId, { status: parseInt(e.target.value) })}
              disabled={updateTaskLoading}
              className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-background"
            >
              <option value={0}>Todo</option>
              <option value={1}>In Progress</option>
              <option value={2}>Done</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Priority</label>
            <select
              onChange={(e) => onUpdateTask(taskId, { priority: parseInt(e.target.value) })}
              disabled={updateTaskLoading}
              className="mt-1 block w-full px-3 py-2 border border-border rounded-md bg-background"
            >
              <option value={0}>Low</option>
              <option value={1}>Normal</option>
              <option value={2}>High</option>
              <option value={3}>Urgent</option>
            </select>
          </div>
        </div>
      </div>

      {/* Comments Section */}
      <div className="border-t border-border pt-4">
        <h4 className="font-medium mb-4">Comments ({comments.length})</h4>

        {/* Add Comment */}
        <div className="mb-4">
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, "comment")}
            placeholder="Add a comment..."
            className="w-full p-2 border border-border rounded-md resize-none"
            rows={2}
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={handleAddComment}
              disabled={!commentText.trim() || addCommentLoading}
              className="bg-primary text-primary-foreground px-3 py-1 rounded text-sm disabled:opacity-50"
            >
              {addCommentLoading ? "Adding..." : "Add Comment"}
            </button>
          </div>
        </div>

        {/* Comments List */}
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {comments.length === 0 ? (
            <p className="text-muted-foreground text-sm">No comments yet</p>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="bg-background p-3 rounded border">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm font-medium">{comment.authorName || "Unknown User"}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(comment.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm">{comment.body}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
